// Core packages
import {
  CdkDragDrop,
  moveItemInArray
} from "@angular/cdk/drag-drop";
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize, take } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { ActivationListDialogComponent } from 'src/app/modules/settings/components/activation-list-dialog/activation-list-dialog.component';
import { StructureSettingOptionsDialogComponent } from 'src/app/modules/settings/components/structure-setting-options-dialog/structure-setting-options-dialog.component';
import { SettingsService } from "src/app/modules/settings/settings.service";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";

/**
 * Script start
 */
@Component({
  selector: "app-structure-setting-detail-dialog",
  templateUrl: "./structure-setting-detail-dialog.component.html",
  styleUrls: ["./structure-setting-detail-dialog.component.scss"],
})
export class StructureSettingDetailDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = true; // Used to view/hide form loading spinner
  title = "";

  // BEGIN - Edit item form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  subForm: FormGroup = new FormGroup({});
  newSubForm: FormGroup = new FormGroup({});
  model: any = {};
  subModel: any = {};
  newSubModel: any = {};
  fields: FormlyFieldConfig[] = [];
  subFields: FormlyFieldConfig[] = [];
  newSubFields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  subOptions: FormlyFormOptions = {};
  newSubOptions: FormlyFormOptions = {};

  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  printModuleBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.printModule"),
    spinnerSize: 19,
    raised: false,
    stroked: false,
    flat: true,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-print",
      inline: true,
    },
  };
  printQrBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.printLabel"),
    spinnerSize: 19,
    raised: false,
    stroked: false,
    flat: true,
    fab: false,
    buttonColor: "accent",
    spinnerColor: "accent",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-qrcode",
      inline: true,
    },
  };
  // END - Edit item form

  newSubformIsVisible = false;
  subformIsVisible = false;
  documents: any[] = [];
  // True until the list is confirmed equal to the server copy after a save:
  // while true, the saves that resend the whole list are blocked
  documentsOutOfSync = false;
  subElementTitle = "";

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<StructureSettingDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private configService: ConfigService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private settingsService: SettingsService
  ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // // Get logged user data
    // this.subscriptions.push(
    //   this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    // );
    // this.subscriptions.push(
    //   this.authService.loggedUserPermissions$.subscribe(
    //     (res) => (this.loggedUserPermissions = res)
    //   )
    // );
    if (
      this.data &&
      this.data.item &&
      this.data.item.translation &&
      this.data.item.translation.it
    ) {
      this.title = this.data.item.translation.it;
      if (
        this.data.item.formConfig && 
        Array.isArray(this.data.item.formConfig) &&
        this.data.item.formConfig.length &&
        this.data.item.formConfig[0].fieldGroup
      ) {
        // this.documents = this.data.item.formConfig[0].fieldGroup;
        this.data.item.formConfig[0].fieldGroup.forEach((el: any) => {
          this.documents.push(el);
        });
      }
    }

    this.initForm();
  }
  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    this.fields = [];
    this.model = this.data.item;

    if (this.data && this.data.item) {
      this.fields = [
        {
          fieldGroupClassName: "row",
          fieldGroup: [
            {
              key: "elementType",
              defaultValue: "object",
              templateOptions: {
                type: "hidden",
              },
            },
            {
              key: "structure",
              defaultValue: "",
              templateOptions: {
                type: "hidden",
              },
            },
            // {
            //   className: "col-12 col-md-12 col-xl-12",
            //   type: "select",
            //   key: "elementType",
            //   templateOptions: {
            //     label: this.translateService.instant("settings.document.group"),
            //     placeholder: this.translateService.instant("settings.document.group"),
            //     required: true,
            //     multiple: false,
            //     default: "object",
            //     options: [
            //       {
            //         value: "object",
            //         label: "Facoltativo",
            //       },
            //       {
            //         value: "array",
            //         label: "Dipendente dalla mappatura",
            //       },
            //     ],
            //   },
            // }
            {
              className: "col-12 col-md-5 col-xl-5",
              type: "input",
              key: "translation.it",
              defaultValue: "",
              templateOptions: {
                label: this.translateService.instant("settings.document.it"),
                placeholder: this.translateService.instant(
                  "settings.document.it"
                ),
                required: true,
                minLength: 2,
                maxLength: 100,
              },
            },
            {
              className: "col-12 col-md-5 col-xl-5",
              type: "input",
              key: "translation.en",
              defaultValue: "",
              templateOptions: {
                label: this.translateService.instant("settings.document.en"),
                placeholder: this.translateService.instant(
                  "settings.document.en"
                ),
                required: true,
                minLength: 2,
                maxLength: 100,
              },
            },
            {
              className: "col-12 col-md-2 col-xl-2",
              type: "select",
              key: "deleted",
              templateOptions: {
                label: this.translateService.instant(
                  "settings.document.deleted"
                ),
                placeholder: this.translateService.instant(
                  "settings.document.deleted"
                ),
                required: true,
                multiple: false,
                default: false,
                options: [
                  {
                    value: false,
                    label: "No",
                  },
                  {
                    value: true,
                    label: "Sì",
                  },
                ],
              },
            },
          ],
        },
      ];
    }

    this.dialogIsLoading = false;
  }

  /**
   * Handle dialog Submit
   *
   * @since 1.0.0
   */
  onFormSubmit(data?: any): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsTouched();
      });
      console.warn("form", this.form);
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }
    // Activate submit button loading status
    this.formBtnOptions.active = true;

    if (this.form.value) {
      const dataToSend = {
        subContent: {
          deleted: this.form.value.deleted,
          translation: this.form.value.translation,
        },

        // documents: this.documents.map((el: any) => {
        //   return {
        //     _id: el._id,
        //     translation: el.translation,
        //     deleted: el.deleted,
        //     mandatory: el.mandatory,
        //     contract: el.contract,
        //     key: el.key,
        //     schemaType: el.schemaType,
        //     expirationNotice:
        //       el.expirationNotice === null ? "" : el.expirationNotice,
        //   };
        // }),
      };

      this.subscriptions.push(
        this.settingsService
          .updateStructure(
            this.data.condoStructureTemplateId,
            this.data.areaId,
            this.data.subAreaId,
            dataToSend
          )
          .pipe(finalize(() => (this.formBtnOptions.active = false)))
          .subscribe(
            (res) => {
              if (res.status) {
                this.toastrService.success(res.message);
                // this.dialogRef.close({
                //   save: "success"
                // });
              }
            },
            (err) => {
              this.helperService.handleFormlyError(this.form, err);
            }
          )
      );
    }
  }

  /**
   * Handle dialog denial
   *
   * @since 1.0.0
   */
  onClose(data?: any): void {
    this.dialogRef.close();
  }

  /**
   * Handle drag&drop
   *
   * @since 1.0.0
   */
  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.documents, event.previousIndex, event.currentIndex);
  }

  optionsManagement(model: any): void {
    const dialogRef = this.dialog.open(StructureSettingOptionsDialogComponent, {
      data: {
        options: model.options,
      },
      width: "70%",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (res.status) {
            if (this.newSubformIsVisible) {
              this.newSubModel.options = res.data.options;
            } else {
              this.subModel.options = res.data;
            }
            
          }
          // this.onGetCondoStructure();
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handle save document template third level
   *
   * @since 1.0.0
   */
  onSubTitleClicked(item: any): void {
    if (!item.type || item.type === 'file-uploader-structure' || item.type === 'repeat') {
      return;
    }
    if (
      item.templateOptions && 
      item.templateOptions.translationLabel && 
      item.templateOptions.translationLabel.it
      ){
      this.subElementTitle = item.templateOptions.translationLabel.it;
    }

    this.newSubformIsVisible = false;
    let type = "";
    if (item.key === "note") {
      type = "note";
    } else if (item.type === "select") {
      type = "select";
    } else if (item.type === "textarea") {
      type = "textarea";
    } else if (item.templateOptions.type && item.templateOptions.type === "number") {
      type = "number";
    } else if (item.type === "input" && item.key === "nome") {
      type = "name";
    } else if (item.type === "select-structure") {
      type = "select-structure";
    } else {
      type = "string";
    }
    
    const objItem = {
      _id: item._id,
      key: item.key,
      deleted: item.deleted,
      required: item.templateOptions.required,
      type,
      translation: item.templateOptions.translationLabel,
      options: item.templateOptions.options,
    }
    this.subModel = objItem;
    this.subFields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            key: "_id",
            defaultValue: "",
            templateOptions: {
              type: "hidden",
            },
          },
          {
            key: "key",
            defaultValue: "",
            templateOptions: {
              type: "hidden",
            },
          },
        ],
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            type: "input",
            key: "translation.it",
            templateOptions: {
              label: this.translateService.instant("settings.document.it"),
              placeholder: this.translateService.instant(
                "settings.document.it"
              ),
              required: true,
              minLength: 2,
              maxLength: 500,
            },
          },
        ],
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            type: "input",
            key: "translation.en",
            templateOptions: {
              label: this.translateService.instant("settings.document.en"),
              placeholder: this.translateService.instant(
                "settings.document.en"
              ),
              required: true,
              minLength: 2,
              maxLength: 500,
            },
          },
        ],
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12 col-md-2 col-xl-2",
            type: "select",
            key: "deleted",
            templateOptions: {
              label: this.translateService.instant(
                "settings.document.deleted"
              ),
              placeholder: this.translateService.instant(
                "settings.document.deleted"
              ),
              required: true,
              multiple: false,
              default: false,
              options: [
                {
                  value: false,
                  label: "No",
                },
                {
                  value: true,
                  label: "Sì",
                },
              ],
            },
          },
          {
            className: "col-12 col-md-2 col-xl-2",
            type: "select",
            key: "required",
            templateOptions: {
              label: this.translateService.instant(
                "settings.document.required"
              ),
              placeholder: this.translateService.instant(
                "settings.document.required"
              ),
              required: true,
              multiple: false,
              default: false,
              options: [
                {
                  value: false,
                  label: "No",
                },
                {
                  value: true,
                  label: "Sì",
                },
              ],
            },
          },
          {
            className: "col-12 col-md-2 col-xl-2",
            type: "select",
            key: "type",
            templateOptions: {
              label: this.translateService.instant(
                "settings.document.type.title"
              ),
              placeholder: this.translateService.instant(
                "settings.document.type.title"
              ),
              required: true,
              multiple: false,
              default: false,
              options: [
                {
                  value: "name",
                  label: this.translateService.instant(
                    "settings.document.type.name"
                  ),
                },
                {
                  value: "note",
                  label: this.translateService.instant(
                    "settings.document.type.note"
                  ),
                },
                {
                  value: "number",
                  label: this.translateService.instant(
                    "settings.document.type.number"
                  ),
                },
                {
                  value: "string",
                  label: this.translateService.instant(
                    "settings.document.type.text"
                  ),
                },
                {
                  value: "textarea",
                  label: this.translateService.instant(
                    "settings.document.type.longText"
                  ),
                },
                {
                  value: "select",
                  label: this.translateService.instant(
                    "settings.document.type.select"
                  ),
                },
                {
                  value: "select-structure",
                  label: this.translateService.instant(
                    "settings.document.type.selectStructure"
                  ),
                },
                {
                  value: "datepicker",
                  label: this.translateService.instant(
                    "settings.document.type.date"
                  ),
                },
              ],
            },
          },
          {
            className: "col-12 col-md-4 col-xl-4",
            type: 'input',
            key: 'defaultValue',
            defaultValue: 'Nessuna',
            templateOptions: {
              label: this.translateService.instant("settings.document.type.defaultValue"),
              placeholder: this.translateService.instant(
                "settings.document.type.defaultValue"
              ),
              required: false,
              minLength: 0,
              maxLength: 200,
            },
            hideExpression: (model) => model.type !== 'textarea',
          },
          {
            className: "col-12 col-md-4 col-xl-4",
            type: 'button',
            key: 'openOptions',
            templateOptions: {
              btnType: "accent",
              tooltip: "tooltip",
              text: "Gestione opzioni",
              icon: {
                fontSet: "fas",
                fontIcon: "fa-filter",
              },
              onClick: ($event) =>
                this.optionsManagement(this.subModel),
            },
            hideExpression: (model) => model.type !== 'select',
          },
        ]
      },
    ];
    this.subformIsVisible = true;
  }

  /**
   * Handle save document template third level
   *
   * @since 1.0.0
   */
  onSaveSubDocumentTemplate(): void {
    if (this.documentsOutOfSync) {
      this.toastrService.error(
        this.translateService.instant("settings.structure.fieldsOutOfSync.saveBlocked")
      );
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant(
          "settings.document.dialog.saveMessage"
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.dialogIsLoading = true;
        const dataToSend = {
          subContent: {
            deleted: this.data.item.deleted,
            defaultValue: this.data.item.defaultValue,
            translation: this.data.item.translation,
            elementType: this.data.item.elementType,
            structure: this.data.item.structure,
          },
          // Position in the array = new order. Both flags are resent with their
          // current value because the V1 back-end assigns whatever arrives (a
          // missing `required` is deleted). No `type`/`options`: the back-end would
          // reassign the field's type, key and options from them.
          documents: this.documents.map((el: any) => {
            return {
              _id: el._id,
              deleted: el.deleted,
              required: el.templateOptions && el.templateOptions.required,
              defaultValue: el.defaultValue,
              key: el.key,
            };
          }),
        };
        this.subscriptions.push(
          this.settingsService
            .updateStructure(
              this.data.condoStructureTemplateId,
              this.data.areaId,
              this.data.subAreaId,
              dataToSend
            )
            .pipe(finalize(() => (this.formBtnOptions.active = false)))
            .subscribe(
              (res) => {
                // this.onGetDocument();
                this.toastrService.success(res.message);
                if (res.status) {
                  this.onReloadDocuments();
                } else {
                  this.dialogIsLoading = false;
                }
              },
              (err) => {
                // Choose one of the following error handling method.
                // 1) First one show a message right under the form fields
                //    (if the form is properly setted)
                // 2) Second one is used to show a mat-error under each field
                //    in the dynamic form (is meant to be used only in dynamicForm)
                // 3) Third one show toastr notifications for each error
                // 4) Fourth one is used to show mat-error under each field
                //    in the ngx-formly form (is mean to be used only with ngx-formly)
                // this.helperService.handleFormError(form, err);
                // this.helperService.handleDynamicFormError(form, err);
                this.helperService.handleError(err);
                this.dialogIsLoading = false;
              }
            )
        );
      });
  }

  /**
   * Handle add new element of third level
   *
   * @since 1.0.0
   */
  onAddSubDocumentTemplate(): void {
    this.subformIsVisible = false;

    this.newSubModel = [];
    this.newSubFields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            key: "_id",
            defaultValue: "",
            templateOptions: {
              type: "hidden",
            },
          },
          {
            key: "key",
            defaultValue: "",
            templateOptions: {
              type: "hidden",
            },
          },
        ],
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            type: "input",
            key: "translation.it",
            templateOptions: {
              label: this.translateService.instant("settings.document.it"),
              placeholder: this.translateService.instant(
                "settings.document.it"
              ),
              required: true,
              minLength: 2,
              maxLength: 500,
            },
          },
        ],
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            type: "input",
            key: "translation.en",
            templateOptions: {
              label: this.translateService.instant("settings.document.en"),
              placeholder: this.translateService.instant(
                "settings.document.en"
              ),
              required: true,
              minLength: 2,
              maxLength: 500,
            },
          },
        ],
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12 col-md-2 col-xl-2",
            type: "select",
            key: "deleted",
            templateOptions: {
              label: this.translateService.instant(
                "settings.document.deleted"
              ),
              placeholder: this.translateService.instant(
                "settings.document.deleted"
              ),
              required: true,
              multiple: false,
              default: false,
              options: [
                {
                  value: false,
                  label: "No",
                },
                {
                  value: true,
                  label: "Sì",
                },
              ],
            },
          },
          {
            className: "col-12 col-md-2 col-xl-2",
            type: "select",
            key: "required",
            templateOptions: {
              label: this.translateService.instant(
                "settings.document.required"
              ),
              placeholder: this.translateService.instant(
                "settings.document.required"
              ),
              required: true,
              multiple: false,
              default: false,
              options: [
                {
                  value: false,
                  label: "No",
                },
                {
                  value: true,
                  label: "Sì",
                },
              ],
            },
          },
          {
            className: "col-12 col-md-2 col-xl-2",
            type: "select",
            key: "type",
            templateOptions: {
              label: this.translateService.instant(
                "settings.document.type.title"
              ),
              placeholder: this.translateService.instant(
                "settings.document.type.title"
              ),
              required: true,
              multiple: false,
              default: false,
              options: [
                {
                  value: "name",
                  label: this.translateService.instant(
                    "settings.document.type.name"
                  ),
                },
                {
                  value: "note",
                  label: this.translateService.instant(
                    "settings.document.type.note"
                  ),
                },
                {
                  value: "number",
                  label: this.translateService.instant(
                    "settings.document.type.number"
                  ),
                },
                {
                  value: "string",
                  label: this.translateService.instant(
                    "settings.document.type.text"
                  ),
                },
                {
                  value: "textarea",
                  label: this.translateService.instant(
                    "settings.document.type.longText"
                  ),
                },
                {
                  value: "select",
                  label: this.translateService.instant(
                    "settings.document.type.select"
                  ),
                },
                {
                  value: "select-structure",
                  label: this.translateService.instant(
                    "settings.document.type.selectStructure"
                  ),
                },
                {
                  value: "datepicker",
                  label: this.translateService.instant(
                    "settings.document.type.date"
                  ),
                },
              ],
            },
          },
          {
            className: "col-12 col-md-4 col-xl-4",
            type: 'input',
            key: 'defaultValue',
            defaultValue: 'Nessuna',
            templateOptions: {
              label: this.translateService.instant("settings.document.type.defaultValue"),
              placeholder: this.translateService.instant(
                "settings.document.type.defaultValue"
              ),
              required: false,
              minLength: 0,
              maxLength: 200,
            },
            hideExpression: (model) => model.type !== 'textarea',
          },
          {
            className: "col-12 col-md-4 col-xl-4",
            type: 'button',
            key: 'openOptions',
            templateOptions: {
              btnType: "accent",
              tooltip: "tooltip",
              text: "Gestione opzioni",
              icon: {
                fontSet: "fas",
                fontIcon: "fa-filter",
              },
              onClick: ($event) =>
                this.optionsManagement(this.newSubModel),
            },
            hideExpression: (model) => model.type !== 'select',
          },
        ]
      },
      
    ];

    this.newSubformIsVisible = true;
  }

  /**
   * Handle dialog Submit
   *
   * @since 1.0.0
   */
  onSubFormSubmit(data?: any): void {
    if (this.documentsOutOfSync) {
      this.toastrService.error(
        this.translateService.instant("settings.structure.fieldsOutOfSync.saveBlocked")
      );
      return;
    }
    if (this.subForm.invalid) {
      Object.keys(this.subForm.controls).forEach((key) => {
        this.subForm.controls[key].markAsTouched();
      });
      console.warn("form", this.subForm);
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }
    // Activate submit button loading status
    this.formBtnOptions.active = true;

    if (this.subForm.value) {
      const dataToSend = {
        subContent: {
          deleted: this.data.item.deleted,
          translation: this.data.item.translation,
        },
        documents: this.documents.map((el: any) => {
          if (el._id.toString() === this.subForm.value._id.toString()) {
            return {
              _id: el._id,
              key: el.key,
              translation: this.subForm.value.translation,
              deleted: this.subForm.value.deleted,
              defaultValue: this.subForm.value.defaultValue,
              required: this.subForm.value.required,
              type: this.subForm.value.type,
              options: el.templateOptions.options,
            };
          }
          return {
            _id: el._id,
            key: el.key,
            translation: el.templateOptions.translationLabel,
            deleted: el.deleted,
            defaultValue: el.defaultValue,
            required: el.templateOptions.required,
            type: el.type,
            options: el.templateOptions.options,
          };
        }),
      };

      this.dialogIsLoading = true;
      this.subscriptions.push(
        this.settingsService
          .updateStructure(
            this.data.condoStructureTemplateId,
            this.data.areaId,
            this.data.subAreaId,
            dataToSend
          )
          .pipe(finalize(() => (this.formBtnOptions.active = false)))
          .subscribe(
            (res) => {
              if (res.status) {
                this.subformIsVisible = false;
                this.toastrService.success(res.message);
                // this.dialogRef.close({
                //   save: "success"
                // });
                this.onReloadDocuments();
              } else {
                this.dialogIsLoading = false;
              }
            },
            (err) => {
              this.helperService.handleFormlyError(this.form, err);
            }
          )
      );
    }
  }

  /**
   * Rebuild the field list from the server copy of this sub-area.
   *
   * The list "Salva" and the field "Salva" resend every field with the values
   * held in `documents`: if the list is not realigned after a save, the next
   * save in the same session reverts what the previous one changed. The V1 PUT
   * replies only { status, message }, so the template is read again. Until
   * this succeeds those two saves stay blocked (`documentsOutOfSync`); the
   * warning above the list offers a retry.
   *
   * @since 3.0.0
   */
  onReloadDocuments(): void {
    this.documentsOutOfSync = true;
    this.dialogIsLoading = true;
    this.subscriptions.push(
      this.settingsService
        .getStructure()
        .pipe(finalize(() => (this.dialogIsLoading = false)))
        .subscribe(
          (res) => {
            // ApiService emits a network error (status 0) as a value
            if (!res || !res.status) {
              this.toastrService.error(
                this.translateService.instant("common.errors.server_down")
              );
              return;
            }
            const subArea = this.findSubArea(res.condoStructureTemplate);
            if (!subArea) {
              // The sub-area is no longer where the dialog was opened
              // (e.g. moved to another area in the meantime)
              this.toastrService.error(
                this.translateService.instant("common.errors.element_not_exists")
              );
              return;
            }
            // As in ngOnInit: a sub-area without formConfig has no fields yet
            const formConfig = subArea.formConfig;
            const fieldGroup =
              Array.isArray(formConfig) &&
              formConfig.length > 0 &&
              formConfig[0] &&
              Array.isArray(formConfig[0].fieldGroup)
                ? formConfig[0].fieldGroup
                : [];
            // "Attivazioni" reads the fields from data.item. Only this property
            // is replaced: data.item stays the model of the form above
            this.data.item.formConfig = formConfig;
            // The field form shares its options array with the field it was
            // opened on: close it instead of leaving it on a replaced object
            this.subformIsVisible = false;
            this.documents = fieldGroup.slice();
            this.documentsOutOfSync = false;
          },
          () => {
            // getStructure() has already shown the error
          }
        )
    );
  }

  /**
   * This dialog's sub-area in the templates returned by getStructure(), or
   * undefined when it is not there.
   *
   * @since 3.0.0
   */
  private findSubArea(templates: any): any {
    const hasId = (el: any, id: any): boolean =>
      !!el && !!el._id && !!id && el._id.toString() === id.toString();

    const template = Array.isArray(templates)
      ? templates.find((el: any) => hasId(el, this.data.condoStructureTemplateId))
      : undefined;
    const area =
      template && Array.isArray(template.area)
        ? template.area.find((el: any) => hasId(el, this.data.areaId))
        : undefined;
    return area && Array.isArray(area.content)
      ? area.content.find((el: any) => hasId(el, this.data.subAreaId))
      : undefined;
  }

  /**
   * Handle dialog Submit
   *
   * @since 1.0.0
   */
  onNewSubFormSubmit(data?: any): void {
    if (this.newSubForm.invalid) {
      Object.keys(this.newSubForm.controls).forEach((key) => {
        this.newSubForm.controls[key].markAsTouched();
      });
      console.warn("form", this.newSubForm);
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }
    // Activate submit button loading status
    this.formBtnOptions.active = true;

    if (this.newSubForm.value) {
      const dataToSend = {
        documents: {
          _id: this.newSubForm.value._id,
          key: this.newSubForm.value.key,
          translation: this.newSubForm.value.translation,
          deleted: this.newSubForm.value.deleted,
          defaultValue: this.subForm.value.defaultValue,
          required: this.newSubForm.value.required,
          type: this.newSubForm.value.type,
          options: this.newSubModel.options,
        },
      };

      this.dialogIsLoading = true;
      this.subscriptions.push(
        this.settingsService
          .createStructure(
            this.data.condoStructureTemplateId,
            this.data.areaId,
            this.data.subAreaId,
            dataToSend
          )
          .pipe(finalize(() => (this.formBtnOptions.active = false)))
          .subscribe(
            (res) => {
              this.dialogIsLoading = false;
              if (res.status) {
                this.newSubformIsVisible = false;
                this.documents = res.thirdLevelDocs;
                this.toastrService.success(res.message);
                // Re-read anyway: "Attivazioni" reads the fields from data.item
                this.onReloadDocuments();
              }
            },
            (err) => {
              this.helperService.handleFormlyError(this.form, err);
            }
          )
      );
    }
  }

  onOpenActivations(): void {
    const dialogRef = this.dialog.open(ActivationListDialogComponent, {
      data: this.data,
      width: "90%",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          // this.onGetCondoStructure();
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }
}
